import { useForm } from '@refinedev/antd';
import { Button, Flex, Form, Input, InputNumber, Radio } from 'antd';
import { OptionProps } from 'antd/es/select';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import { PRODUCT_TYPES, ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';
import {
  SERVICE_ORDER_ITEM_RESOLUTION,
  ServiceOrderItem,
  serviceOrderItemResolutionString,
} from '@/model/ServiceOrder/ServiceOrderItem';

import useServiceOrderTechnicianItem from './useServiceOrderTechnicianItem';

interface ServiceOrderTechnicianItemFormProps {
  item?: ServiceOrderItem;
  onSave: (updates: Partial<ServiceOrderItem>) => void;
  onCancel: () => void;
}

const ServiceOrderTechnicianItemForm = (props: ServiceOrderTechnicianItemFormProps) => {
  const { t } = useTranslation();
  const { item, onSave, onCancel } = props;
  const { formProps } = useForm<ServiceOrderItem>({
    resource: 'service-order-items',
    id: item?.id,
    action: 'edit',
    queryOptions: {
      enabled: false,
    },
    successNotification: {
      message: t('Item editado con éxito'),
      type: 'success',
    },
  });
  const { productType, productsSelectProps, serialNumbersSelectProps } =
    useServiceOrderTechnicianItem(formProps, item);
  const isSerializedType =
    productType === PRODUCT_TYPES.UNIT || productType === PRODUCT_TYPES.DEVICE;
  const elementPlaceholder = useMemo(() => {
    if (productType === PRODUCT_TYPES.PRODUCT) return t('Seleccione un producto');
    if (productType === PRODUCT_TYPES.UNIT) return t('Seleccione una unidad');
    if (productType === PRODUCT_TYPES.DEVICE) return t('Seleccione un dispositivo');

    return t('Seleccione un tipo de contenido');
  }, [productType, t]);
  const elementFormName = useMemo(() => {
    if (productType === 1) return 'unidad';
    if (productType === 2) return 'device';
  }, [productType]);
  const resolution = Form.useWatch('resolution_status', formProps.form);
  // Opciones dinámicas a partir del objeto de traducciones con claves numéricas del enum.
  const resolutionOptions = useMemo(
    () =>
      Object.entries(serviceOrderItemResolutionString)
        .map(([resolution, label]) => ({
          value: Number(resolution) as SERVICE_ORDER_ITEM_RESOLUTION,
          label: t(label),
        }))
        .filter(option => option.value !== SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_AUTOMATIC),
    [t],
  );

  return (
    <Form {...formProps} layout="vertical" onFinish={onSave}>
      <Form.Item label={t('Tipo de contenido')} name="product_type">
        <AntdSelect
          options={Object.values(ProductTypeStrings).map((v, index) => ({
            label: t(v),
            value: index,
          }))}
          disabled={!!item}
          allowClear
        />
      </Form.Item>
      <Form.Item
        label={t('Producto')}
        name={'product'}
        rules={[{ required: true, message: elementPlaceholder }]}>
        <AntdSelect
          {...productsSelectProps}
          disabled={productType === undefined || !!item}
          placeholder={elementPlaceholder}
          onChange={(_, opt) => {
            productsSelectProps.onChange?.(_, opt);
            if (!Array.isArray(opt)) {
              formProps?.form?.setFieldValue('name', opt?.label ?? opt?.value);
            }
          }}
          allowClear
          showSearch
        />
      </Form.Item>
      <Form.Item hidden name={'name'}>
        <Input />
      </Form.Item>
      {isSerializedType && (
        <>
          <Form.Item label={t('Número de serie')} name={elementFormName}>
            <AntdSelect
              {...serialNumbersSelectProps}
              placeholder={elementPlaceholder}
              allowClear
              showSearch
              onChange={(v, opt) => {
                serialNumbersSelectProps.onChange?.(v, opt);
                formProps?.form?.setFieldValue('serial_number', (opt as OptionProps)?.label);
              }}
            />
          </Form.Item>
          <Form.Item hidden label={t('Número de serie')} name={'serial_number'}>
            <Input />
          </Form.Item>
        </>
      )}
      <Form.Item label={t('Cantidad')} name={'used_amount'} rules={[{ required: true }]}>
        <InputNumber
          placeholder="cantidad"
          style={{ width: '100%' }}
          disabled={productType != 0}
          min={0}
        />
      </Form.Item>
      <Form.Item
        name="resolution_status"
        label={t('Resolución')}
        layout="horizontal"
        rules={[{ required: true, message: t('Es necesario indicar una resolución') }]}>
        {/* Evitamos parseos de keys string para no perder labels/opciones en el Radio.Group. */}
        <Radio.Group
          options={resolutionOptions}
          /* Distribución vertical sin utilizar la prop no soportada */
          style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}
        />
      </Form.Item>
      {resolution === SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_PERITAGE && (
        <Form.Item
          label={t('En garantía')}
          name="in_warranty"
          valuePropName="checked"
          layout="horizontal"
          rules={[{ required: true, message: t('Es necesario indicar el estado de la garantía') }]}>
          <Radio.Group
            options={[
              { label: t('Sí'), value: true },
              { label: t('No'), value: false },
            ]}
            /* Distribución vertical sin utilizar la prop no soportada */
            style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}
          />
        </Form.Item>
      )}
      <Flex align="center" justify="end" gap={8}>
        <Button onClick={onCancel}>{t('Cancelar')}</Button>
        <Button type="primary" htmlType="submit">
          {t('Guardar')}
        </Button>
      </Flex>
    </Form>
  );
};

export default ServiceOrderTechnicianItemForm;
